// src/hooks/useForexPositions.js
// Manages open forex positions — localStorage-backed CRUD.
// Call enrichPositions(positions, getQuote) wherever live P&L is needed.

import { useState, useEffect, useCallback } from "react";
import { getPipSize, getPairMeta } from "../data/forexPairs";

const STORAGE_KEY = "nuvport_forex_positions";

function load() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]"); }
  catch { return []; }
}

/**
 * Enrich a position with live P&L data from a quote.
 * Pure function — call in the rendering component.
 *
 * @param {object} pos      — raw position from storage
 * @param {object|null} quote — { price } from stockData.getQuote or fetchQuote
 * @returns enriched position object
 */
export function enrichPosition(pos, quote) {
  const currentRate = quote?.price ?? null;
  if (currentRate == null) return { ...pos, currentRate: null, pnl: null, pnlPct: null, pips: null };

  const sign    = pos.direction === "LONG" ? 1 : -1;
  const pipSize = getPipSize(pos.pair);
  const pips    = +(sign * (currentRate - pos.entryRate) / pipSize).toFixed(1);
  const pnlPct  = +(sign * (currentRate - pos.entryRate) / pos.entryRate * 100).toFixed(3);
  const pnl     = +(pos.notional * pnlPct / 100).toFixed(2);

  return { ...pos, currentRate, pips, pnl, pnlPct };
}

/**
 * @returns {{ positions, addPosition, removePosition, clearAll }}
 */
export function useForexPositions() {
  const [positions, setPositions] = useState(load);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(positions)); }
    catch { /* storage full */ }
  }, [positions]);

  /**
   * @param {{ pair: string, direction: "LONG"|"SHORT", entryRate: number, notional: number }} pos
   */
  const addPosition = useCallback((pos) => {
    const meta = getPairMeta(pos.pair);
    setPositions(prev => [...prev, {
      id:        Date.now(),
      pair:      pos.pair,
      label:     meta.label,
      base:      meta.base,
      quote:     meta.quote,
      direction: pos.direction,
      entryRate: +pos.entryRate,
      notional:  +pos.notional,
      openedAt:  new Date().toISOString(),
    }]);
  }, []);

  const removePosition = useCallback((id) => {
    setPositions(prev => prev.filter(p => p.id !== id));
  }, []);

  const clearAll = useCallback(() => setPositions([]), []);

  return { positions, addPosition, removePosition, clearAll };
}
